/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, Injector, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { AddListComponent } from 'app/shared/components/add-list/add-list.component';
import { IconActionButtonEventMessage } from 'app/shared/components/icon-action-button-list/icon-action-button-list-message.model';

import { UsersService } from 'app/modules/configuration/users/user-management/services/users.service';
import { OrderSearchListService } from 'app/modules/order/order-search/order-search/services/order-search-list.service';
import { ComponentItem } from 'app/shared/base/aside/component-item';

import { FormMode } from 'app/shared/enumeration/form-mode';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { Subject, takeUntil } from 'rxjs';
import { OrderListType } from '../models/order-list-type';
import { OrderSearchListItemViewModel } from '../models/order-search-list-item-view.model';
import { RefundFormComponent } from '../refund-form/refund-form.component';

@Component({
	selector: 'order-search-list',
	templateUrl: './order-search-list.component.html',
	styleUrls: ['./order-search-list.component.scss'],
	standalone: false,
})
export class OrderSearchListComponent extends AddListComponent {
	@Input() isOrganiserUser = false;
	@Input() customerId: number;
	@Input() showListActions = true;
	private ngUnsubscribe: Subject<boolean> = new Subject();

	get listType(): OrderListType {
		if (this.customerId) {
			return OrderListType.CustomerOrderList;
		}
		if (this.isOrganiserUser) {
			return OrderListType.OrganiserOrderList;
		}
		return OrderListType.DefaultOrderList;
	}

	get displayedColumns(): string[] {
		if (this.listType === OrderListType.CustomerOrderList) {
			return [
				'hasRefundedTickets',
				'orderNumber',
				'orderDate',
				'entryCount',
				'nonEntryCount',
				'glampingCount',
				'entryDetails',
				'nonEntryDetails',
				'glampingDetails',
				'paymentPlan',
				'paymentMethod',
				'event',
				'eventDate',
				'tickets',
				'organiser',
				'venue',
				'eventStatus',
				'genre',
				'faceValueTotal',
				'bookingFee',
				'venueLevyFee',
				'discount',
				'processingFee',
				'dispatchMethodFee',
				'cancellationProtectionFee',
				'charityDonationAmount',
				'orderTotal',
				'orbitProcessingFee',
				'orbitCancellationProtectionFee',
				'orbitServiceFee',
				'orbitDispatchMethodFee',
				'orbitReceives',
				'venueReceives',
				'organiserProcessingFee',
				'organiserTicketIncome',
				'organiserReceives',
				'partnerCancellationProtectionFee',
				'charityReceives',
				'discountCode',
				'membershipCode',
				'orbitContactPreference',
				'organiserContactPreference',
				'actions',
			];
		}

		if (this.listType === OrderListType.OrganiserOrderList) {
			return [
				'hasRefundedTickets',
				'orderNumber',
				'orderDate',
				'entryCount',
				'nonEntryCount',
				'glampingCount',
				'entryDetails',
				'nonEntryDetails',
				'glampingDetails',
				'customer',
				'paymentPlan',
				'paymentMethod',
				'event',
				'eventDate',
				'tickets',
				'organiser',
				'venue',
				'eventStatus',
				'genre',
				'orderTotal',
				'orbitContactPreference',
				'organiserContactPreference',
			];
		}

		return [
			'hasRefundedTickets',
			'orderNumber',
			'orderDate',
			'entryCount',
			'nonEntryCount',
			'glampingCount',
			'entryDetails',
			'nonEntryDetails',
			'glampingDetails',
			'customer',
			'paymentPlan',
			'paymentMethod',
			'event',
			'eventDate',
			'tickets',
			'organiser',
			'venue',
			'eventStatus',
			'genre',
			'faceValueTotal',
			'bookingFee',
			'venueLevyFee',
			'discount',
			'processingFee',
			'dispatchMethodFee',
			'cancellationProtectionFee',
			'charityDonationAmount',
			'orderTotal',
			'orbitProcessingFee',
			'orbitCancellationProtectionFee',
			'orbitServiceFee',
			'orbitDispatchMethodFee',
			'orbitReceives',
			'venueReceives',
			'organiserProcessingFee',
			'organiserTicketIncome',
			'organiserReceives',
			'partnerCancellationProtectionFee',
			'charityReceives',
			'discountCode',
			'membershipCode',
			'orbitContactPreference',
			'organiserContactPreference',
			'actions',
		];
	}
	constructor(
		private route: ActivatedRoute,
		public readonly listService: OrderSearchListService,
		private asideService: AsideService,
		private injector: Injector,
		protected usersService: UsersService,
		protected confirmationDialogService: ConfirmationDialogService,
	) {
		super(listService);

		this.defaultSortProperty = 'orderDate';
		this.defaultSortDirection = 'desc';
		listService.loadState();
		this.loadAfterInit = false;
		this.configureLocalLoadingOverlay();
	}

	private orderColumnWidth(key: string): string {
		if (['entryDetails', 'nonEntryDetails', 'glampingDetails', 'paymentMethod', 'tickets', 'eventStatus', 'genre'].includes(key)) return '200px';
		if (['paymentPlan'].includes(key)) return '190px';
		if (['discountCode', 'membershipCode', 'orbitContactPreference', 'organiserContactPreference'].includes(key)) return '180px';
		if (['event', 'organiser', 'venue'].includes(key)) return '150px';
		if (['orderNumber', 'orderDate', 'eventDate'].includes(key)) return '140px';
		if (['entryCount', 'nonEntryCount', 'glampingCount'].includes(key)) return '140px';
		if (key === 'customer') return '130px';
		return '100px';
	}

	private formatDate(value: unknown): string {
		if (!value) return '';
		const date = new Date(value as string | number | Date);
		return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB');
	}

	get orderSearchListService() {
		return this.service as OrderSearchListService;
	}

	ngAfterViewInit() {
		this.asideService.asideClose.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
			if (data === 'update') {
				this.reloadList();
			}
		});
		super.ngAfterViewInit();
	}

	ngOnInit() {
		super.ngOnInit();
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next(true);
		this.ngUnsubscribe.complete();
		super.ngOnDestroy();
	}

	exportList() {
		this.listService.customerId = this.customerId;
		this.blockUI.start();
		this.listService
			.exportToExcel()
			.then(() => {
				this.blockUI.stop();
			})
			.catch((error) => {
				this.blockUI.stop();
			});
	}

	addNew() {}

	doIconButtonAction(message: IconActionButtonEventMessage) {
		switch (message.action) {
			case 'refund': {
				this.asideService.setAndOpen(new ComponentItem(RefundFormComponent, { itemId: message.data, formMode: FormMode.Create }), this.injector);
				break;
			}
			case 'sendConfirmation': {
				this.confirmationDialogService.openConfirmationDialog('Confirm Send', 'Are you sure you want to send this Booking Confirmation Email?', () => {
					this.blockUI.start();
					this.listService
						.sendOrderConfirmationEmail(message.data)
						.then(() => {})
						.finally(() => {
							this.blockUI.stop();
						});
				});

				break;
			}
			default: {
				console.log('Unsupported action: ' + message.action);
				break;
			}
		}
	}
}
